import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  AccountMemberPicker,
  type AccountMember,
} from "@/components/account/AccountMemberPicker";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ApiError,
  createToolGrant,
  deleteToolGrant,
  fetchToolGrants,
  updateToolGrant,
  type ToolGrant,
  type ToolGrantCandidate,
  type ToolGrantRole,
} from "@/lib/brand-tool-grants-api";
import { userAvatarInitial } from "@/lib/user-display";

type WhoCanUsePanelProps = {
  accountId: string;
  brandId: string;
  product?: string;
  productName?: string;
  brandName?: string;
  /** 外层页面已有标题时隐藏面板内标题区 */
  hideHeader?: boolean;
};

/** 候选人无账户角色时，pill 用 member 占位 */
const CANDIDATE_ROLE_FALLBACK: AccountMember["role"] = "member";

/** 将 ToolGrantCandidate 映射为选人器 AccountMember（头像等缺省填合理默认） */
function candidateToAccountMember(candidate: ToolGrantCandidate): AccountMember {
  const displayName = candidate.display_label?.trim() || null;
  return {
    id: candidate.user_id,
    user_id: candidate.user_id,
    role: CANDIDATE_ROLE_FALLBACK,
    kind: "internal",
    disabled: false,
    user: {
      display_name: displayName,
      display_label: candidate.display_label,
      email: candidate.email,
      phone: candidate.phone,
      avatar_url: null,
      avatar_initial: userAvatarInitial({
        email: candidate.email,
        display_name: displayName,
        avatar_initial: "",
      }),
    },
  };
}

export function WhoCanUsePanel({
  accountId,
  brandId,
  product = "journey",
  productName = "客户旅程",
  brandName,
  hideHeader = false,
}: WhoCanUsePanelProps) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toolEnabled, setToolEnabled] = useState(false);
  const [grants, setGrants] = useState<ToolGrant[]>([]);
  const [candidates, setCandidates] = useState<ToolGrantCandidate[]>([]);
  const [roles, setRoles] = useState<ToolGrantRole[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  /** 选中键为 user_id，与 AccountMemberPicker / handleAdd 对齐 */
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [addRole, setAddRole] = useState("member");
  const [removeTarget, setRemoveTarget] = useState<ToolGrant | null>(null);

  const defaultAddRole = useCallback((nextRoles: ToolGrantRole[]) => {
    return nextRoles.find((r) => r.value === "member")?.value ?? nextRoles[0]?.value ?? "member";
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchToolGrants(accountId, brandId, product);
      setToolEnabled(payload.tool_enabled);
      setGrants(payload.grants);
      setCandidates(payload.candidates);
      setRoles(payload.roles);
      setAddRole(defaultAddRole(payload.roles));
      setSelectedIds([]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, [accountId, brandId, product, defaultAddRole]);

  useEffect(() => {
    void load();
  }, [load]);

  const unggranted = useMemo(() => candidates.filter((c) => !c.granted), [candidates]);
  const pickerMembers = useMemo(
    () => unggranted.map(candidateToAccountMember),
    [unggranted],
  );

  function openAddDialog() {
    if (unggranted.length === 0) {
      toast.info("账户内成员均已授权，或暂无其他成员。新人需先加入账户「成员」。");
      return;
    }
    setSelectedIds([]);
    setAddRole(defaultAddRole(roles));
    setAddOpen(true);
  }

  async function handleAdd() {
    if (selectedIds.length === 0) {
      toast.error("请先勾选要加入的成员");
      return;
    }
    setBusy(true);
    try {
      for (const userId of selectedIds) {
        await createToolGrant(accountId, brandId, product, { user_id: userId, role: addRole });
      }
      toast.success("已加入选中的成员");
      setAddOpen(false);
      setSelectedIds([]);
      await load();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "加入失败");
    } finally {
      setBusy(false);
    }
  }

  async function handleRoleChange(grant: ToolGrant, role: string) {
    setBusy(true);
    try {
      await updateToolGrant(accountId, brandId, product, grant.id, { role });
      toast.success("角色已更新");
      await load();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "更新失败");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveConfirm() {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await deleteToolGrant(accountId, brandId, product, removeTarget.id);
      toast.success("已移除");
      setRemoveTarget(null);
      await load();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "移除失败");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">加载中…</p>;
  }

  if (!toolEnabled) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card px-6 py-10 text-center">
        <p className="text-sm text-foreground">本品牌尚未开通{productName}</p>
        <p className="mt-1 text-xs text-muted-foreground">开通后才能设置谁能使用。</p>
        <Button asChild className="mt-4" variant="outline" size="sm">
          <Link to={`/accounts/${accountId}/brands/${brandId}`}>前往品牌面板开通</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {hideHeader ? null : (
          <div className="space-y-1">
            <h2 className="text-lg font-medium">成员权限</h2>
            <p className="text-sm text-muted-foreground">
              谁能使用{productName}
              {brandName ? `（${brandName}）` : ""}。开通本工具时，启用者会自动成为首个管理员。
            </p>
          </div>
        )}
        <Button
          type="button"
          className={hideHeader ? "ml-auto" : undefined}
          disabled={busy}
          onClick={openAddDialog}
        >
          <Plus className="size-4" />
          加入成员
        </Button>
      </div>

      {grants.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card px-6 py-10 text-center">
          <p className="text-sm text-foreground">还没有人被授权使用</p>
          <p className="mt-1 text-xs text-muted-foreground">
            若刚开通工具，刷新后应看到启用者；也可点右上角从账户成员中勾选加入。
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          {grants.map((grant) => (
            <li key={grant.id} className="flex flex-wrap items-center gap-3 bg-card px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{grant.user.display_label}</p>
                <p className="text-xs text-muted-foreground">
                  {[grant.user.email, grant.user.phone].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <Select
                value={grant.role}
                disabled={busy}
                onValueChange={(value) => void handleRoleChange(grant, value)}
              >
                <SelectTrigger className="h-8 w-28" aria-label="角色">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setRemoveTarget(grant)}
              >
                移除
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground">
        新人需先出现在账户「成员」中；此处不提供邮箱/手机邀请。
      </p>

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (busy) return;
          setAddOpen(open);
          if (!open) setSelectedIds([]);
        }}
      >
        <DialogContent className="flex max-h-[min(85vh,640px)] flex-col gap-4 overflow-hidden sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>加入成员</DialogTitle>
            <DialogDescription>
              勾选能使用{productName}的成员，并选择加入后的角色。
            </DialogDescription>
          </DialogHeader>

          <AccountMemberPicker
            mode="multiple"
            members={pickerMembers}
            value={selectedIds}
            onChange={setSelectedIds}
            disabled={busy}
            emptyText="暂无待加入的成员"
            searchPlaceholder="搜索昵称、邮箱、手机…"
            className="min-h-0 flex-1"
          />

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">加入后的角色</Label>
            <Select value={addRole} disabled={busy} onValueChange={setAddRole}>
              <SelectTrigger className="h-8 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="sm:justify-between">
            <p className="self-center text-xs text-muted-foreground">已选 {selectedIds.length} 人</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => setAddOpen(false)}>
                取消
              </Button>
              <Button
                type="button"
                disabled={busy || selectedIds.length === 0}
                onClick={() => void handleAdd()}
              >
                {busy ? "加入中…" : "确认加入"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget != null}
        onOpenChange={(open) => {
          if (!open && !busy) setRemoveTarget(null);
        }}
        title="移除授权"
        description={
          removeTarget
            ? `确定移除「${removeTarget.user.display_label}」对${productName}的使用权限吗？`
            : null
        }
        confirmLabel="移除"
        tone="destructive"
        confirming={busy}
        onConfirm={() => void handleRemoveConfirm()}
      />
    </div>
  );
}
